import { getApiBase } from '@membersyncpro/feedback';
export { getApiBase };

// ── Shared response types ────────────────────────────────────────────────

export type MemberStatus = 'active' | 'suspended' | 'inactive';

export interface Member {
  id: string;
  organization_id: string | null;
  name: string;
  email: string | null;
  phone: string | null;
  monthly_dues_cents: number;
  joined_at: string;
  status: MemberStatus;
  notes: string | null;
  created_at: string;
  updated_at: string;
  // Decorated by GET /v1/members
  last_payment_at: string | null;
  last_payment_cents: number;
  paid_this_month_cents: number;
  is_paid_this_month: boolean;
  // Visit-based billing. null when the member has never had a day-based payment
  // (still on the monthly-dues model).
  days_purchased: number;
  days_used: number;
  balance_days: number | null;
  // Surfaced from the read-only subscriptions / class_passes tables (mig 0013).
  // null + 0 when no rows exist.
  active_subscription_status: string | null;
  active_subscription_plan_name: string | null;
  active_subscription_plan_amount_cents: number | null;
  available_class_passes: number;
  date_of_birth: string | null;
  usaw_id: string | null;
  usaw_expires_on: string | null;
  usaw_membership_type: string | null;
}

export interface Event {
  id: string;
  name: string;
  type: string;
  sanctioning_body: string | null;
  starts_at: string;
  ends_at: string | null;
  location_name: string | null;
  location_address: string | null;
  url: string | null;
  notes: string | null;
  status: 'scheduled' | 'completed' | 'cancelled';
  created_at: string;
  updated_at: string;
  registered_count?: number;
}

export interface EventRegistration {
  id: string;
  event_id: string;
  member_id: string;
  member_name?: string;
  member_status?: MemberStatus;
  status: 'registered' | 'competed' | 'withdrew' | 'cancelled';
  weight_class: string | null;
  bracket: string | null;
  result: string | null;
  placement: number | null;
  wins: number | null;
  losses: number | null;
  notes: string | null;
  registered_at: string;
}

export interface Subscription {
  id: string;
  source: string;
  source_id: string | null;
  status: string;
  billing_partner: string | null;
  billing_partner_id: string | null;
  interval: string | null;
  interval_count: number | null;
  current_period_end_at: string | null;
  expires_on: string | null;
  started_at: string | null;
  source_updated_at: string | null;
  plan_name: string | null;
  plan_amount_cents: number | null;
  plan_currency: string | null;
  paid_session_id: number | null;
}

export interface ClassPass {
  id: string;
  source: string;
  source_id: string | null;
  status: string;
  used_at: string | null;
  log: string | null;
  source_created_at: string | null;
}

export interface Payment {
  id: string;
  member_id: string;
  member_name?: string;
  amount_cents: number;
  paid_at: string;
  method: 'cash' | 'card' | 'transfer' | 'other';
  covers_month: string | null;
  days_added: number | null;
  notes: string | null;
  recorded_by_user_id: string | null;
}

export interface DashboardStats {
  month: string; // YYYY-MM
  members: { active: number; suspended: number; inactive: number };
  payments_this_month: { count: number; total_cents: number };
  payments_today: { count: number; total_cents: number };
  overdue: { count: number; total_owed_cents: number };
  checkins_today: { count: number };
  day_passes_today: { count: number; total_cents: number };
  sales_today: { count: number; total_cents: number };
  recent_payments: Payment[];
  upcoming_sessions: Array<{
    id: string; starts_at: string; ends_at: string; capacity: number;
    status: 'scheduled' | 'cancelled' | 'completed';
    class_name: string; coach_name: string | null; registered_count: number;
  }>;
  low_stock: Array<{ id: string; name: string; stock_qty: number; price_cents: number }>;
  unsigned_waivers: { count: number };
  notification_status_7d: Array<{ status: string; n: number }>;
  upcoming_events: Array<{
    id: string; name: string; type: string; sanctioning_body: string | null;
    starts_at: string; ends_at: string | null; location_name: string | null;
    registered_count: number;
  }>;
  mrr: {
    active_count: number;
    monthly_cents: number;
    by_plan: Array<{ plan: string; n: number; cents: number }>;
  };
  birthdays_this_week: Array<{ id: string; name: string; date_of_birth: string; dob_md: string }>;
  usaw_expiring: Array<{ id: string; name: string; usaw_id: string | null; usaw_expires_on: string; usaw_membership_type: string | null; days_left: number }>;
}

export interface MemberProfile {
  member: Member;
  payments: Array<{ id: string; amount_cents: number; paid_at: string; method: string; covers_month: string | null; days_added: number | null; notes: string | null }>;
  checkins: Array<{ id: string; checked_in_at: string; source: string; notes: string | null }>;
  class_registrations: Array<{
    id: string; status: string; registered_at: string;
    session_id: string; starts_at: string; ends_at: string;
    class_id: string; class_name: string;
  }>;
  waiver_signatures: Array<{
    id: string; signer_name: string; signed_at: string; ip_address: string | null;
    waiver_id: string; waiver_title: string; waiver_version: number;
  }>;
  notifications: Array<{ id: string; channel: 'email'|'sms'; template: string; recipient: string; status: 'sent'|'skipped'|'failed'; sent_at: string }>;
  store_sales: Array<{ id: string; qty: number; total_cents: number; payment_method: string; sold_at: string; product_name: string }>;
  subscriptions: Subscription[];
  class_passes: ClassPass[];
}

export interface CheckIn {
  id: string;
  member_id: string | null;
  day_pass_id: string | null;
  visitor_name: string | null;
  checked_in_at: string;
  source: 'public' | 'dashboard' | 'kiosk' | 'qr';
  notes: string | null;
  member_name?: string | null;
  member_status?: MemberStatus | null;
  day_pass_name?: string | null;
  class_id?: string | null;
  class_name?: string | null;
  class_color?: string | null;
}

export interface DayPass {
  id: string;
  visitor_name: string;
  visitor_email: string | null;
  visitor_phone: string | null;
  price_cents: number;
  method: 'cash' | 'card' | 'transfer' | 'comp' | 'other';
  valid_for_date: string;
  notes: string | null;
  created_at: string;
}

export interface Coach {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  bio: string | null;
  hourly_rate_cents: number;
  status: 'active' | 'inactive';
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface ClassDef {
  id: string;
  name: string;
  description: string | null;
  default_duration_min: number;
  default_capacity: number;
  default_coach_id: string | null;
  default_coach_name?: string | null;
  color: string;
  created_at: string;
}

export interface ClassSession {
  id: string;
  class_id: string;
  class_name: string;
  class_color: string;
  coach_id: string | null;
  coach_name: string | null;
  starts_at: string;
  ends_at: string;
  capacity: number;
  status: 'scheduled' | 'cancelled' | 'completed';
  notes: string | null;
  registered_count: number;
}

export interface ClassRegistration {
  id: string;
  session_id: string;
  member_id: string | null;
  member_name: string | null;
  visitor_name: string | null;
  status: 'registered' | 'attended' | 'no_show' | 'cancelled';
  registered_at: string;
}

// ── Tiny fetch helper ────────────────────────────────────────────────────

type FetchOpts = {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  bearerToken?: string | null;
  query?: Record<string, string | number | undefined>;
};

// Thrown by apiCall on non-2xx responses. `body` carries the parsed JSON
// error envelope so callers can read structured fields (reason codes,
// state snapshots) instead of just the stringified summary.
export class ApiError extends Error {
  status: number;
  body: Record<string, unknown>;
  constructor(status: number, body: Record<string, unknown>, msg: string) {
    super(msg);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

export async function apiCall<T>(path: string, opts: FetchOpts = {}): Promise<T> {
  const url = new URL(`${getApiBase()}${path}`);
  if (opts.query) {
    for (const [k, v] of Object.entries(opts.query)) {
      if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, String(v));
    }
  }
  const headers: Record<string, string> = {};
  if (opts.body) headers['Content-Type'] = 'application/json';
  if (opts.bearerToken) headers.Authorization = `Bearer ${opts.bearerToken}`;
  const init: RequestInit = { method: opts.method ?? 'GET', headers };
  if (opts.body !== undefined) init.body = JSON.stringify(opts.body);
  const res = await fetch(url.toString(), init);
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    const errStr = typeof body.error === 'string' ? body.error : `HTTP ${res.status}`;
    throw new ApiError(res.status, body, `${errStr} (${res.status})`);
  }
  // Some endpoints return JSON, some plain {ok:true}
  return (await res.json()) as T;
}

// ── Currency helpers ─────────────────────────────────────────────────────

export function formatCents(cents: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
}

export function dollarsToCents(dollarStr: string): number {
  // accept "12", "12.5", "12.50", "$12.50", "1,200.00"
  const cleaned = dollarStr.replace(/[^\d.-]/g, '');
  const n = Number(cleaned);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100);
}
